import { memo, ReactNode } from 'react';
import TreeItem from './TreeItem/TreeItem';
import type { TreeItemLevel } from './TreeItem/TreeItem.types';

export type LeafRowProps = {
  optionLabel: string;
  properties: { path: string; checked: boolean | 'indeterminate' };
  handleClickOption: (path: string) => void;
  badgeContent?: ReactNode;
  hideCheckbox?: boolean;
  icon?: ReactNode;
  isDisabled?: boolean;
  tooltipText?: string;
  forceTooltip?: boolean;
  level?: TreeItemLevel;
  tabIndex?: number;
};

function LeafRowInner({
  optionLabel,
  properties,
  handleClickOption,
  badgeContent,
  hideCheckbox,
  icon,
  isDisabled = false,
  tooltipText,
  forceTooltip = false,
  level,
  tabIndex,
}: LeafRowProps) {
  const isSelected = properties.checked === true;

  return (
    <TreeItem
      label={optionLabel}
      level={level}
      tabIndex={tabIndex}
      hasChildren={false}
      showCheckbox={!hideCheckbox}
      checked={properties.checked}
      onCheck={isDisabled ? undefined : () => handleClickOption(properties.path)}
      badge={badgeContent}
      icon={icon}
      isSelected={isSelected}
      isDisabled={isDisabled}
      tooltipText={tooltipText}
      forceTooltip={forceTooltip}
      onClick={hideCheckbox && !isDisabled ? () => handleClickOption(properties.path) : undefined}
      data-testid={`${properties.path}-leafRow`}
      checkboxDataTestId={`${properties.path}-checkbox`}
    />
  );
}

// Custom comparator to handle the `properties` object reference changing on every tree rebuild.
// Compares the actual values that affect rendering rather than object identity.
const LeafRow = memo(LeafRowInner, (prev, next) => {
  return (
    prev.properties.path === next.properties.path &&
    prev.properties.checked === next.properties.checked &&
    prev.optionLabel === next.optionLabel &&
    prev.hideCheckbox === next.hideCheckbox &&
    prev.isDisabled === next.isDisabled &&
    prev.tooltipText === next.tooltipText &&
    prev.forceTooltip === next.forceTooltip &&
    prev.level === next.level &&
    prev.tabIndex === next.tabIndex &&
    prev.badgeContent === next.badgeContent &&
    prev.handleClickOption === next.handleClickOption
  );
});

export default LeafRow;
